import { useState } from "react";
import api from "../api/api";
import { useNavigate } from "react-router-dom";
import { X, LogIn, UserPlus } from "lucide-react";

export default function LoginModal({ isOpen, onClose, setUser, setToast }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const validate = () => {
        let newErrors = {};

        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        if (mode !== "forgot" && !form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});
        setSuccess("");

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {

            // FORGOT PASSWORD
            if (mode === "forgot") {
                await api.post("/forgot-password", {
                    email: form.email,
                });

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                setLoading(false);
                return;
            }

            // REGISTER

            if (mode === "register") {
                await api.post("/register", {
                    name: form.name,
                    email: form.email,
                    password: form.password,
                    password_confirmation: form.password,
                });

                setMode("login");
                setLoading(false);
                onClose();
                return;
            }

            // LOGIN
            const res = await api.post("/login", {
                email: form.email,
                password: form.password,
            });

            const userData = res.data.user;

            localStorage.setItem("user", JSON.stringify(userData));
            setUser(userData);

            setLoading(false);

            setToast({
                type: "success",
                message: `Welcome ${userData.name}, you have successfully logged in 🎉`,
            });

            onClose();
            navigate("/profile");

        } catch (err) {
            console.error(err);
            setLoading(false);

            if (mode === "forgot") {
                setErrors({
                    general: "Unable to send the reset link. Please try again later."
                });
            } else {
                setErrors({
                    general: "Invalid email or password."
                });
            }
        }
    };





    return (
        <div className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-black/70
        backdrop-blur-md
        px-5
    ">

            {/* MODAL CARD */}
            <div
                className="
            relative
            w-full
            max-w-md
            bg-[#FCFAF5]
            dark:bg-[#111827]
            rounded-[32px]
            shadow-2xl
            border
            border-[#D9BB74]/20
            p-8
            animate-[fadeIn_.3s_ease-out]
        "
            >

                {/* CLOSE */}
                <button
                    onClick={onClose}
                    className="
                absolute
                top-5
                right-5
                w-10
                h-10
                rounded-full
                flex
                items-center
                justify-center
                bg-black/5
                dark:bg-white/10
                hover:bg-[#D9BB74]
                hover:text-black
                transition-all
                duration-300
            "
                >
                    <X size={18} />
                </button>


                {/* HEADER */}
                <div className="text-center mb-8">

                    <div
                        className="
                    w-16
                    h-16
                    mx-auto
                    mb-5
                    rounded-2xl
                    bg-gradient-to-br
                    from-[#D9BB74]
                    to-[#B89442]
                    flex
                    items-center
                    justify-center
                    text-black
                    shadow-lg
                "
                    >
                        {
                            mode === "login" ? (
                                <LogIn size={26} />
                            ) : mode === "register" ? (
                                <UserPlus size={26} />
                            ) : (
                                <span className="text-3xl">🔑</span>
                            )
                        }
                    </div>


                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {mode === "register"
                            ? "Create Account"
                            : mode === "forgot"
                                ? "Forgot Password"
                                : "Welcome Back"}
                    </h2>


                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        {mode === "register"
                            ? "Join UAE Dubai Hotels and discover luxury stays"
                            : mode === "forgot"
                                ? "Enter your email to receive a password reset link."
                                : "Login to manage your hotel bookings"}
                    </p>

                </div>



                {/* ERROR */}
                {errors.general && (
                    <div
                        className="
                    mb-5
                    text-sm
                    text-red-600
                    bg-red-50
                    dark:bg-red-900/20
                    p-3
                    rounded-xl
                "
                    >
                        {errors.general}
                    </div>
                )}

                {success && (
                    <div
                        className="
            mb-5
            rounded-xl
            bg-green-50
            dark:bg-green-900/20
            p-3
            text-sm
            text-green-700
            dark:text-green-400
        "
                    >
                        {success}
                    </div>
                )}



                {/* FORM */}
                <form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                >


                    {/* NAME */}
                    {mode === "register" && (
                        <div>

                            <input
                                name="name"
                                placeholder="Full Name"
                                onChange={handleChange}
                                className="
                            w-full
                            px-5
                            py-4
                            rounded-2xl
                            border
                            border-gray-200
                            dark:border-gray-700
                            bg-white
                            dark:bg-gray-900
                            outline-none
                            transition
                            focus:border-[#D9BB74]
                            focus:ring-2
                            focus:ring-[#D9BB74]/20
                        "
                            />

                            {errors.name && (
                                <p className="text-red-500 text-xs mt-2">
                                    {errors.name}
                                </p>
                            )}

                        </div>
                    )}



                    {/* EMAIL */}
                    <div>

                        <input
                            name="email"
                            type="email"
                            placeholder="Email Address"
                            onChange={handleChange}
                            className="
                        w-full
                        px-5
                        py-4
                        rounded-2xl
                        border
                        border-gray-200
                        dark:border-gray-700
                        bg-white
                        dark:bg-gray-900
                        outline-none
                        transition
                        focus:border-[#D9BB74]
                        focus:ring-2
                        focus:ring-[#D9BB74]/20
                    "
                        />


                        {errors.email && (
                            <p className="text-red-500 text-xs mt-2">
                                {errors.email}
                            </p>
                        )}

                    </div>




                    {/* PASSWORD */}
                    {mode !== "forgot" && (
                        <div>
                            <input
                                name="password"
                                type="password"
                                placeholder="Password"
                                onChange={handleChange}
                                className="
            w-full
            px-5
            py-4
            rounded-2xl
            border
            border-gray-200
            dark:border-gray-700
            bg-white
            dark:bg-gray-900
            outline-none
            transition
            focus:border-[#D9BB74]
            focus:ring-2
            focus:ring-[#D9BB74]/20
        "
                            />

                            {errors.password && (
                                <p className="text-red-500 text-xs mt-2">
                                    {errors.password}
                                </p>
                            )}

                            {mode === "login" && (
                                <div className="flex justify-end mt-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setErrors({});
                                            setSuccess("");
                                            setMode("forgot");
                                        }}
                                        className="
                    text-sm
                    font-medium
                    text-[#B89442]
                    hover:text-[#D9BB74]
                    transition
                "
                                    >
                                        Forgot Password?
                                    </button>
                                </div>
                            )}

                        </div>
                    )}




                    {/* BUTTON */}
                    <button
                        disabled={loading}
                        className="
                    w-full
                    py-4
                    rounded-2xl
                    font-bold
                    text-black
                    bg-gradient-to-r
                    from-[#D9BB74]
                    to-[#B89442]
                    hover:shadow-xl
                    hover:-translate-y-1
                    transition-all
                    duration-300
                    disabled:opacity-60
                "
                    >

                        {loading
                            ? "Please wait..."
                            : mode === "register"
                                ? "Create Account"
                                : mode === "forgot"
                                    ? "Send Reset Link"
                                    : "Login"}

                    </button>


                </form>




                {/* SWITCH */}
                <div
                    className="
                text-center
                mt-7
                text-sm
                text-gray-500
                dark:text-gray-400
            "
                >

                    {mode === "login" && (
                        <button
                            onClick={() => setMode("register")}
                            className="text-[#B89442] font-semibold hover:text-[#D9BB74]"
                        >
                            New here? Create account
                        </button>
                    )}

                    {mode === "register" && (
                        <button
                            onClick={() => setMode("login")}
                            className="text-[#B89442] font-semibold hover:text-[#D9BB74]"
                        >
                            Already have an account? Login
                        </button>
                    )}

                    {mode === "forgot" && (
                        <button
                            onClick={() => {
                                setSuccess("");
                                setMode("login");
                            }}
                            className="text-[#B89442] font-semibold hover:text-[#D9BB74]"
                        >
                            ← Back to Login
                        </button>
                    )}

                </div>


            </div>

        </div>
    );
}